import { useState } from "react";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Mail,
  Phone,
  User,
  Users,
  MessageSquare,
  Send,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useReveal } from "../hooks/useReveal";
import { useLanguage } from "../lib/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  guests: 2,
  message: "",
};

export const BookingForm = () => {
  const ref = useReveal();
  const { t } = useLanguage();
  const [data, setData] = useState(initial);
  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.phone) {
      toast.error(t("book_err_required"));
      return;
    }
    if (!checkIn || !checkOut) {
      toast.error(t("book_err_dates"));
      return;
    }
    if (checkOut <= checkIn) {
      toast.error(t("book_err_range"));
      return;
    }
    setSubmitting(true);
    const payload = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      check_in: format(checkIn, "yyyy-MM-dd"),
      check_out: format(checkOut, "yyyy-MM-dd"),
      guests: Number(data.guests) || 1,
      message: data.message || "",
    };
    try {
      await axios.post(`${API}/bookings`, payload);
      try {
        await fetch("https://formsubmit.co/ajax/athnera@yahoo.gr", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            _subject: `New booking request — ${payload.name}`,
            _template: "table",
            _captcha: "false",
            name: payload.name,
            email: payload.email,
            phone: payload.phone,
            check_in: payload.check_in,
            check_out: payload.check_out,
            guests: payload.guests,
            message: payload.message,
          }),
        });
      } catch (_err) {
        /* email fallback failed; booking already saved */
      }
      toast.success(t("book_success"));
      setData(initial);
      setCheckIn(undefined);
      setCheckOut(undefined);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("book_err_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const fieldBase =
    "bg-white border border-[#0a192f]/15 rounded-xl h-12 px-4 text-[#0a192f] placeholder:text-[#6b705c]/70 focus-visible:ring-[#1a5b73]";

  return (
    <section
      id="contact"
      data-testid="booking-section"
      className="py-24 md:py-36 bg-[#f3efe6]"
    >
      <div ref={ref} className="reveal max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#6b705c] mb-6">
              {t("book_eyebrow")}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#0a192f] leading-[1.02]">
              {t("book_title_a")}{" "}
              <em className="italic text-[#1a5b73]">{t("book_title_b")}</em>.
            </h2>
            <p className="mt-8 text-[#4a4a4a] font-light leading-relaxed text-base md:text-lg max-w-md">
              {t("book_intro")}
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              <div className="bg-white/60 border border-[#0a192f]/10 rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b705c]">
                  {t("book_checkin")}
                </div>
                <div className="font-serif text-lg text-[#0a192f] mt-1">
                  14:00 – 21:00
                </div>
              </div>
              <div className="bg-white/60 border border-[#0a192f]/10 rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b705c]">
                  {t("book_checkout")}
                </div>
                <div className="font-serif text-lg text-[#0a192f] mt-1">
                  08:00 – 12:00
                </div>
              </div>
            </div>
            <div className="mt-12 space-y-5 text-[#0a192f]">
              <div className="flex items-start gap-4">
                <Phone className="h-4 w-4 mt-1 text-[#1a5b73]" strokeWidth={1.5} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b705c]">
                    {t("book_phone_lbl")}
                  </div>
                  <div className="font-serif text-xl">697 400 3022</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="h-4 w-4 mt-1 text-[#1a5b73]" strokeWidth={1.5} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b705c]">
                    {t("book_email_lbl")}
                  </div>
                  <a
                    href="mailto:athnera@yahoo.gr"
                    className="font-serif text-xl text-[#0a192f] hover:text-[#1a5b73] transition-colors"
                  >
                    athnera@yahoo.gr
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="h-4 w-4 mt-1 text-[#1a5b73]" strokeWidth={1.5} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b705c]">
                    {t("book_address_lbl")}
                  </div>
                  <div className="font-serif text-xl leading-snug">
                    Αθηνά Βίλας Ιστρο,
                    <br />
                    Kalo Chorio 721 00, Crete
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="booking-form"
            className="md:col-span-7 bg-white border border-[#0a192f]/10 rounded-3xl p-8 md:p-12 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field icon={User} label={t("book_f_name")}>
                <Input
                  data-testid="booking-input-name"
                  value={data.name}
                  onChange={set("name")}
                  placeholder={t("book_p_name")}
                  className={fieldBase}
                />
              </Field>
              <Field icon={Mail} label={t("book_f_email")}>
                <Input
                  data-testid="booking-input-email"
                  type="email"
                  value={data.email}
                  onChange={set("email")}
                  placeholder={t("book_p_email")}
                  className={fieldBase}
                />
              </Field>
              <Field icon={Phone} label={t("book_f_phone")}>
                <Input
                  data-testid="booking-input-phone"
                  value={data.phone}
                  onChange={set("phone")}
                  placeholder={t("book_p_phone")}
                  className={fieldBase}
                />
              </Field>
              <Field icon={Users} label={t("book_f_guests")}>
                <Input
                  data-testid="booking-input-guests"
                  type="number"
                  min={1}
                  max={20}
                  value={data.guests}
                  onChange={set("guests")}
                  className={fieldBase}
                />
              </Field>
              <Field icon={CalendarIcon} label={t("book_checkin")}>
                <DateField
                  testId="booking-checkin"
                  value={checkIn}
                  onChange={setCheckIn}
                  placeholder={t("book_p_checkin")}
                  disabled={(d) =>
                    d < new Date(new Date().setHours(0, 0, 0, 0))
                  }
                />
              </Field>
              <Field icon={CalendarIcon} label={t("book_checkout")}>
                <DateField
                  testId="booking-checkout"
                  value={checkOut}
                  onChange={setCheckOut}
                  placeholder={t("book_p_checkout")}
                  disabled={(d) =>
                    d < new Date(new Date().setHours(0, 0, 0, 0)) ||
                    (checkIn && d <= checkIn)
                  }
                />
              </Field>
            </div>

            <Field icon={MessageSquare} label={t("book_f_message")}>
              <Textarea
                data-testid="booking-input-message"
                value={data.message}
                onChange={set("message")}
                placeholder={t("book_p_message")}
                rows={4}
                className="bg-white border border-[#0a192f]/15 rounded-xl px-4 py-3 text-[#0a192f] placeholder:text-[#6b705c]/70 focus-visible:ring-[#1a5b73]"
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="booking-submit"
              className="w-full bg-[#1a5b73] hover:bg-[#134456] text-white py-4 rounded-full text-[12px] uppercase tracking-[0.28em] font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-3"
            >
              {submitting ? t("book_submitting") : t("book_submit")}
              <Send className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ icon: Icon, label, children }) => (
  <label className="block">
    <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#6b705c] mb-2">
      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
      {label}
    </span>
    {children}
  </label>
);

const DateField = ({ value, onChange, placeholder, disabled, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        data-testid={`${testId}-trigger`}
        className="w-full text-left bg-white border border-[#0a192f]/15 rounded-xl h-12 px-4 text-[#0a192f] hover:bg-[#faf9f6] transition-colors"
      >
        {value ? (
          <span className="text-sm">{format(value, "EEE, dd MMM yyyy")}</span>
        ) : (
          <span className="text-sm text-[#6b705c]/70">{placeholder}</span>
        )}
      </button>
    </PopoverTrigger>
    <PopoverContent
      data-testid={`${testId}-popover`}
      className="w-auto p-0 bg-white border border-[#0a192f]/10 rounded-2xl"
      align="start"
    >
      <Calendar
        mode="single"
        selected={value}
        onSelect={onChange}
        disabled={disabled}
        initialFocus
      />
    </PopoverContent>
  </Popover>
);

